import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getBusinessById } from '../utils/inviteService';
import { Business } from '../types/business';
import { Loading } from '../components/ui/Loading';

interface BusinessContextType {
    currentBusiness: Business | null;
    businessId: string | undefined;
    loading: boolean;
    refreshBusiness: () => Promise<void>;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export const useBusiness = () => {
    const context = useContext(BusinessContext);
    if (context === undefined) {
        throw new Error('useBusiness must be used within a BusinessProvider');
    }
    return context;
};

export const BusinessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { currentUser } = useAuth();
    const [currentBusiness, setCurrentBusiness] = useState<Business | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchBusiness = async () => {
        if (!currentUser?.businessId) {
            setCurrentBusiness(null);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const business = await getBusinessById(currentUser.businessId);
            setCurrentBusiness(business);
        } catch (error) {
            console.error('Error fetching business:', error);
            setCurrentBusiness(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBusiness();
    }, [currentUser?.businessId]);

    const value = {
        currentBusiness,
        businessId: currentUser?.businessId,
        loading,
        refreshBusiness: fetchBusiness
    };

    return (
        <BusinessContext.Provider value={value}>
            {children}
        </BusinessContext.Provider>
    );
};
